// Single source for mood colors - referenced by both the top-level `mood`
// namespace and `semantic.mood*` so the two can never drift apart.
const moodPalette = {
  down: "#6B8ABF",
  neutral: "#9AA5A1",
  calm: "#7FB59E",
  happy: "#E8B464",
  frustrated: "#D17A7A",
};

const colors = {
  brand: {
    ink: "#8FC9C0",
    tint: "#D9D1EE",
    navy: "#5BAFA7",
    deep: "#7C6AAE",
    tealBlue: "#C7D9C4",
    lightBlue: "#EAF4F2",
    ice: "#F4F1F8",
  },
  mood: moodPalette,
  landing: {
    pageBackground: "#F8FAFC",
    heroText: "#0F172A",
    bodyText: "#334155",
    textStrong: "#0F172A",
    textMuted: "#64748B",
    textSoft: "#64748B",
    deepNavy: "#0F172A",
    midNavy: "#334155",
    cyanPop: "#8FC9C0",
    pastelAqua: "#C7D9C4",
    productHighlight: "#8FC9C0",
    tealLight: "#EAF4F2",
    quickLogHeroStart: "#6FA594",
    quickLogHeroEnd: "#9CC6B8",
    quickLogHeroGlow: "rgba(255, 255, 255, 0.10)",
    surface: "#FFFFFF",
    surfaceSoft: "#F8FAFC",
    surfaceTint: "#FFFFFF",
    panelSoft: "#F4F1F8",
    sageLight: "#F7FBF9",
    borderSoft: "#E2E8F0",
    borderLight: "#E2E8F0",
    borderMedium: "#CBD5E1",
    borderStrong: "#94A3B8",
    borderFocus: "#8FC9C0",
    borderActive: "#5BAFA7",
    lavenderDark: "#B6A7D9",
    shadowSoft: "rgba(0, 0, 0, 0.05)",
    shadowMedium: "rgba(15, 23, 42, 0.05)",
    shadowPanel: "rgba(0, 0, 0, 0.05)",
    shadowStrong: "rgba(15, 23, 42, 0.12)",
    shadowHero: "rgba(143, 201, 192, 0.22)",
    shadowHeroStrong: "rgba(15, 23, 42, 0.14)",
    quoteBadge: "#334155",
    quoteGradientStart: "#D9D1EE",
    quoteGradientEnd: "#F4F1F8",
  },
  // navbarColors.current/pastel are defined and consumed in
  // src/assets/theme/navbarTheme.js (the single source of truth for those
  // two schemes); only `gradient` and the standalone skeleton tint are
  // shared/read from here.
  navbar: {
    gradient: {
      appBarBg: "linear-gradient(90deg, #8FC9C0 0%, #D9D1EE 100%)",
      textColor: "#ffffff",
      hoverColor: "#0F172A",
      skeletonBg: "rgba(255,255,255,0.2)",
      bottomBg: "#8FC9C0",
    },
    loadingSkeletonBg: "rgba(255,255,255,0.2)",
  },
  semantic: {
    ink: "#4A5954",
    ink2: "#7A8A84",
    ink3: "#AAB5AE",
    line: "#E5ECE8",
    line2: "#DDE5E0",
    bg: "#FFFFFF",
    card: "#F7FBF9",
    sage: "#7FB59E",
    sageDeep: "#6FA594",
    sageAction: "#A7C8BC",
    sageActionHover: "#9FBFB4",
    sageActionInk: "#173E35",
    sageSoft: "#DCEADF",
    sageMist: "#EEF2F0",
    behavior: {
      bg: "#F2D4C9",
      ink: "#8B4A36",
      soft: "#F2D4C9",
      mist: "#FAE8E0",
    },
    context: {
      bg: "#E3EAF0",
      ink: "#3A4E66",
      border: "#E3EAF0",
    },
    sleep: {
      bg: "#CFD8E8",
      ink: "#334465",
      soft: "#D6E8E1",
      mist: "#EAF2EE",
      panel: "rgba(207, 216, 232, 0.33)",
      toggleBg: "rgba(207, 216, 232, 0.55)",
      border: "#E5ECE8",
      borderSoft: "#EEF2F0",
      inactiveText: "#7A8A84",
      progressDot: "#334465",
      reminder: "#F59E0B",
    },
    sleepQuality: {
      awful: { bg: "#D17A7A", ink: "#D17A7A" },
      poor: { bg: "#E8B464", ink: "#E8B464" },
      fair: { bg: "#C8B894", ink: "#C8B894" },
      good: { bg: "#9CC6B8", ink: "#9CC6B8" },
      great: { bg: "#7FB59E", ink: "#7FB59E" },
    },
    meds: {
      bg: "#C9E4D8",
      ink: "#2F5A49",
      soft: "#D6E8E1",
      mist: "#EAF2EE",
    },
    categories: {
      meds: { bg: "#C9E4D8", ink: "#2F5A49" },
      sleep: { bg: "#CFD8E8", ink: "#334465" },
      food: { bg: "#F6DED2", ink: "#6B3A24" },
      activity: { bg: "#E3D5EC", ink: "#4A2E5E" },
      behavior: { bg: "#F2CFD4", ink: "#6B2E38" },
      toileting: { bg: "#E8EDC8", ink: "#4C521F" },
    },
    // Day-status marker dots used by MiniCalendar (distinct from the
    // Timeline day-status markers, which source from LOG_TYPES palettes).
    calendarMarkers: {
      logDot: "#2BA7A0",
      incidentDot: "#D14343",
    },
    profileFlow: {
      completionBg: "#EAF2EE",
      completionText: "#6FA594",
      heroGradientStart: "#F5F0E8",
      heroGradientEnd: "#EAF2EE",
      progressFilled: "#6FA594",
      progressPartial: "#9CC6B8",
      progressEmpty: "#E5ECE8",
      eyebrowText: "#7A8A84",
      cardBorder: "#E5ECE8",
      cardBg: "#FFFFFF",
      notStartedBg: "#EEF2F0",
      notStartedText: "#7A8A84",
      inProgressBg: "#F6DED2",
      inProgressText: "#6B3A24",
      doneBg: "#EAF2EE",
      doneText: "#6FA594",
      reviewGradientStart: "#6FA594",
      reviewGradientEnd: "#9CC6B8",
      reviewIconBg: "rgba(255, 255, 255, 0.2)",
      nudgeBg: "#EAF2EE",
      fieldLabel: "#4A5954",
      fieldBg: "#F6F8F6",
      focusBorder: "#6FA594",
      basicsIconBg: "#C9E4D8",
      basicsIconInk: "#2F5A49",
      healthIconBg: "#F6DED2",
      healthIconInk: "#6B3A24",
      medsIconBg: "#CFD8E8",
      medsIconInk: "#334465",
      behaviorIconBg: "#F2CFD4",
      behaviorIconInk: "#6B2E38",
      supportIconBg: "#D9E8E1",
      supportIconInk: "#4E7264",
      photoButtonBg: "#EAF2EE",
      photoButtonText: "#6FA594",
      photoDashedBorder: "#9CC6B8",
      medDeleteBg: "#FEF2F2",
      medDeleteText: "#B91C1C",
      medicationManager: {
        guidanceBg: "#EAF5F1",
        primaryAction: "#338B70",
        primaryActionHover: "#28745D",
        actionText: "#2F7C72",
        disclosureIcon: "#80928C",
        controlBorder: "#D7E0DC",
        controlBorderHover: "#AFC8BE",
        controlHoverBg: "#F8FBF9",
        helperText: "#98A39E",
      },
      careStory: {
        pageBg: "#F8FAF7",
        cardBg: "#FFFFFF",
        cardBorder: "#DDE8E3",
        cardShadow: "rgba(15, 23, 42, 0.08)",
        text: "#17211E",
        muted: "#7C8B85",
        primary: "#5F9A88",
        primaryDark: "#376D60",
        primarySoft: "#E9F2EE",
        sheetHandle: "#DEE8E3",
        warningBg: "#FFF6EC",
        warningBorder: "#F3C99B",
        warningText: "#8A3F16",
        tones: {
          who: {
            headerBg: "#D5EAE2",
            headerInk: "#35695C",
            chipBg: "#E9F2EE",
            chipInk: "#35695C",
            border: "#CFE2DB",
            markerBg: "#C7E4D9",
          },
          hardMoments: {
            headerBg: "#F3CCD3",
            headerInk: "#34486A",
            chipBg: "#F6DDE2",
            chipInk: "#5E3340",
            border: "#EABBC5",
            markerBg: "#F3CCD3",
          },
          whatHelps: {
            headerBg: "#CBE8DA",
            headerInk: "#2F675A",
            chipBg: "#DCEFE8",
            chipInk: "#2F675A",
            border: "#B7DACB",
            markerBg: "#C8E7D8",
          },
          sensory: {
            headerBg: "#CFDAEC",
            headerInk: "#35496A",
            chipBg: "#DFE7F4",
            chipInk: "#35496A",
            border: "#BBC8DD",
            markerBg: "#CFDAEC",
          },
          rhythm: {
            headerBg: "#E4D2EF",
            headerInk: "#4A2F62",
            chipBg: "#EDE1F5",
            chipInk: "#4A2F62",
            border: "#D6C1E5",
            markerBg: "#E4D2EF",
          },
          health: {
            headerBg: "#F7DCCF",
            headerInk: "#7A3F26",
            chipBg: "#F8E6DD",
            chipInk: "#7A3F26",
            border: "#EBC4B2",
            markerBg: "#F7DCCF",
          },
          emergency: {
            headerBg: "#ECF2C9",
            headerInk: "#4D5721",
            chipBg: "#F2F6D9",
            chipInk: "#4D5721",
            border: "#DDE7AF",
            markerBg: "#DDE8D1",
          },
        },
      },
    },
    // Mirrors the top-level `mood` palette (single source above) so
    // callers reading `semantic.mood*` and `mood.*` can never drift apart.
    moodDown: moodPalette.down,
    moodNeutral: moodPalette.neutral,
    moodCalm: moodPalette.calm,
    moodHappy: moodPalette.happy,
    moodFrustrated: moodPalette.frustrated,
    success: "#4CAF50",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
    neutral: "#6B7280",
    surface: "#FFFFFF",
    surfaceSoft: "#F8FAFC",
    borderSoft: "rgba(148, 163, 184, 0.18)",
  },
  roles: {
    careOwner: {
      primary: "#2C74D4",
      secondary: "#A7C7E7",
      accent: "#081F5C",
      name: "Care Owner",
      description: "Primary caregiver with full access",
      icon: "👑",
    },
    carePartner: {
      primary: "#8B5CF6",
      secondary: "#C4B5FD",
      accent: "#4C1D95",
      name: "Care Partner",
      description: "Family member or friend helper",
      icon: "👥",
    },
    caregiver: {
      primary: "#059669",
      secondary: "#A7F3D0",
      accent: "#065F46",
      name: "Caregiver",
      description: "Professional daily care provider",
      icon: "🤝",
    },
    therapist: {
      primary: "#0891B2",
      secondary: "#A5F3FC",
      accent: "#164E63",
      name: "Therapist",
      description: "Licensed healthcare professional",
      icon: "🩺",
    },
  },
  marketing: {
    about: {
      background: "#FFFFFF",
      title: "#059669",
      text: "#333333",
      textMuted: "#666666",
      imageShadow: "rgba(0,0,0,0.12)",
      buttonShadow: "rgba(0,0,0,0.12)",
      buttonHoverShadow: "rgba(0,0,0,0.16)",
    },
    contact: {
      backgroundStart: "#f7fbff",
      backgroundEnd: "#eef6f3",
      paperBg: "#FFFFFF",
      border: "#d9e8df",
      supportBg: "#f5fbf7",
      supportBorder: "#d7eadc",
      title: "#183b2b",
      text: "#365447",
      textMuted: "#4e6c60",
      buttonBg: "#2a7a56",
      buttonHoverBg: "#236648",
    },
  },
  // AuthScreen decoration (sign-in/sign-up page chrome only - excludes
  // provider brand marks like the Google "G" logo, which stay literal).
  auth: {
    pageBg: "#F2F7F4",
    heroGradientStart: "#DCEADF",
    heroGradientEnd: "#F6F8F6",
    dotLavender: "#EAE8F4",
    ctaPrimary: "#5A9F88",
    ctaPrimaryHover: "#4F8D79",
    signUpActiveHover: "#5C9683",
    googleButtonHoverBg: "#F8FAF8",
  },
  // CorrelationDashboard action-link color (Reports/Insights).
  insights: {
    actionText: "#5B8C51",
  },
  // Shared with CareTeamPartnerView.js and CareTeamOwnerView.js, which
  // both defined an identical getRoleConfig() color map independently.
  // Intentionally distinct from `roles.*` (a different palette used
  // elsewhere) - preserved as-is to avoid changing rendered colors.
  careTeamView: {
    carePartner: "#45B7D1",
    caregiver: "#4ECDC4",
    therapist: "#FF6B6B",
    unknownRole: "#94A3B8",
    // CareTeamChildSection.js invite-error and cancel-invite reds.
    // Distinct from `semantic.error` - preserved as-is to avoid changing
    // rendered colors.
    inviteErrorText: "#D32F2F",
    cancelActionText: "#B42318",
  },
  app: {
    container: "#F8FAFC",
    tertiary: {
      main: "#7C6F57",
      light: "#A49592",
      dark: "#727077",
    },
    calendar: {
      background: "#EED8C9",
      accent: "#E99787",
      accentHover: "#d48a7a",
      weekendBg: "#f9f5f2",
      eventDot: "#EB684A",
      todayBg: "#A49592",
      hoverBg: "#fce9e5",
      headerText: "#333333",
    },
    dailyCare: {
      primary: "#6D28D9",
      light: "#8B5CF6",
      dark: "#5B21B6",
      background: "rgba(109, 40, 217, 0.1)",
      hover: "rgba(109, 40, 217, 0.2)",
      progressBackground: "rgba(109, 40, 217, 0.05)",
      progressBorder: "rgba(109, 40, 217, 0.1)",
    },
    dailyLog: {
      chipBg: "#A3B18A",
      hoverIcon: "#EB684A",
      deleteHover: "#F4B860",
    },
    behavior: {
      primary: "#FF9800",
      light: "#FFB74D",
      dark: "#F57C00",
      background: "rgba(255, 152, 0, 0.1)",
      hover: "rgba(255, 152, 0, 0.2)",
    },
    performance: {
      primary: "#4CAF50",
      light: "#81C784",
      dark: "#388E3C",
      background: "rgba(76, 175, 80, 0.1)",
      hover: "rgba(76, 175, 80, 0.2)",
    },
    timeline: {
      incident: "#DC2626",
      dailyHabit: "#059669",
      dailyLog: "#8B5CF6",
      // therapyNote intentionally omitted: light.js sources it from
      // roles.therapist.primary (single source for role-based colors).
      morning: "#0284C7",
      afternoon: "#F59E0B",
      evening: "#7C6F57",
    },
    incident: {
      eatingNutrition: "#22C55E",
      mood: "#F59E0B",
      sleep: "#3B82F6",
      behavioral: "#EF4444",
      sensory: "#8B5CF6",
      painMedical: "#DC2626",
      other: "#6B7280",
    },
    safety: {
      allergy: "#FF9800",
      medication: "#4CAF50",
      allergyBg: "rgba(255, 152, 0, 0.15)",
      medicationBg: "rgba(76, 175, 80, 0.15)",
      allergyBorder: "rgba(255, 152, 0, 0.3)",
      medicationBorder: "rgba(76, 175, 80, 0.3)",
    },
    cards: {
      background: "#FFFFFF",
      shadowSoft: "rgba(0,0,0,0.05)",
      shadowHover: "rgba(0,0,0,0.08)",
      shadowPanel: "rgba(0,0,0,0.05)",
      border: "#E8E2D9",
      modalShadow: "rgba(0, 0, 0, 0.15)",
      paperShadow: "rgba(0, 0, 0, 0.03)",
    },
    text: {
      secondary: "#666666",
      muted: "#888888",
      strong: "#333333",
      darkNeutral: "#04061f",
    },
    dashboard: {
      quickAction: {
        primaryGradientStart: "#8FC9C0",
        primaryGradientEnd: "#5BAFA7",
        primaryHoverStart: "#7FBCB4",
        primaryHoverEnd: "#4F978F",
        primaryShadow: "rgba(143, 201, 192, 0.18)",
        primaryHoverShadow: "rgba(143, 201, 192, 0.3)",
        secondaryBorder: "rgba(143, 201, 192, 0.18)",
        secondaryBg: "#ffffff",
        secondaryText: "#0F172A",
        secondaryHoverBg: "#F7FBF9",
      },
      childHeader: {
        switchBorder: "rgba(143, 201, 192, 0.22)",
        switchBg: "rgba(247, 251, 249, 0.88)",
        switchHoverBg: "rgba(243, 247, 245, 0.95)",
        switchShadow: "rgba(15, 23, 42, 0.04)",
        heroBgStart: "rgba(243,247,245,0.98)",
        heroBgEnd: "rgba(244,241,248,0.96)",
        heroBorder: "rgba(199, 217, 196, 0.55)",
        heroBorderCollapsed: "rgba(226, 232, 240, 0.9)",
        heroShadow: "rgba(143, 201, 192, 0.08)",
        heroShadowCollapsed: "rgba(15, 23, 42, 0.06)",
        avatarShadow: "rgba(15, 23, 42, 0.08)",
        finishProfileBg: "#F7D8DF",
        finishProfileHoverBg: "#F2CAD4",
        finishProfileBorder: "rgba(185, 78, 103, 0.24)",
        finishProfileAccent: "#B94E67",
        allergyColor: "#B45309",
        allergyBorder: "rgba(245, 158, 11, 0.16)",
        supportColor: "#7C6AAE",
        supportBorder: "rgba(217, 209, 238, 0.18)",
      },
    },
  },
};

export default colors;
